import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Edit3,
  ShoppingCart,
  User,
  Calendar,
  Plus,
  Trash2,
  CreditCard,
  Store,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  XCircle,
  MessageSquare,
  Truck,
  QrCode,
  AlertTriangle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  getSaleChangeRequestReasons,
  createEditRequest,
} from "@/Api/saleChangeRequests";
import { getSelectSaleTypes } from "@/Api/saleTypes";
import { getPaymentForms } from "@/Api/sales";
import { getCircuitRoutesData } from "@/Api/circuit_routes";
import { getSelectPaymentAdvances } from "@/Api/paymentAdvances";
import { getClientBalance } from "@/Api/clients";
import { Mask } from "@/utils/mask";
import { SALE_TYPE_ID, PAYMENT_FORMS } from "@/utils/constants";
import { PaymentAdvanceSelectItem } from "@/types/paymentAdvance";
import { AddressSearch } from "@/components/AddressSearch";
import { GoogleMap, loadGoogleMapsScript } from "@/components/GoogleMap";
import { usePermissions } from "@/hooks/usePermissions";
import { useSettings } from "@/hooks/useSettings";
import { getSaleChangeRequestWindow } from "@/utils/saleChangeRequestWindow";
import { MapPin } from "lucide-react";

interface Reason {
  id: number;
  name: string;
  type: string;
}

interface PaymentForm {
  payment_form_id: number;
  value: string;
  payment_advance_id?: number | null;
  payment_form?: {
    id: number;
    name: string;
  };
}

interface Address {
  id?: number;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  latitude?: string | null;
  longitude?: string | null;
}

interface Sale {
  id: number;
  uuid: string;
  total: number | string;
  sale_type_id: number;
  circuit_route_id?: number | null;
  client_id?: number;
  seller_id?: number;
  sale_type?: {
    id: number;
    name: string;
  };
  circuit_route?: {
    id: number;
    title: string;
  } | null;
  client?: {
    uuid?: string;
    name: string;
    has_purchase_voucher?: boolean;
    addresses?: Address[];
  };
  created_at: string;
  payment_forms?: PaymentForm[];
}

interface EditRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: Sale | null;
  onSuccess?: () => void;
}

interface ProposedPaymentForm {
  payment_form_id: number;
  value: number;
  payment_advance_id?: number;
}

interface ProposedAddress {
  addressName: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zip_code: string;
  latitude: number | null;
  longitude: number | null;
  reference?: string;
}

export function EditRequestModal({
  isOpen,
  onClose,
  sale,
  onSuccess,
}: EditRequestModalProps) {
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { isAdmin, isTenantUser } = usePermissions();

  // Garante que as configurações do sistema estejam carregadas ao abrir o modal
  useSettings();

  /**
   * Prazo para solicitar a edição, definido pelo admin em Configurações do
   * Sistema. Admins são isentos, como no backend.
   */
  const requestWindow = getSaleChangeRequestWindow(
    "edit",
    sale?.created_at,
    isAdmin() || isTenantUser()
  );

  const [saleTypes, setSaleTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [paymentFormOptions, setPaymentFormOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [routes, setRoutes] = useState<Array<{ id: number; title: string }>>([]);

  const [proposedSaleTypeId, setProposedSaleTypeId] = useState<number | null>(null);
  const [proposedCircuitRouteId, setProposedCircuitRouteId] = useState<number | null>(null);
  const [proposedPaymentForms, setProposedPaymentForms] = useState<ProposedPaymentForm[]>([]);

  const [newPaymentForm, setNewPaymentForm] = useState<number | null>(null);
  const [newPaymentValue, setNewPaymentValue] = useState<string>("");
  const [paymentAdvances, setPaymentAdvances] = useState<PaymentAdvanceSelectItem[]>([]);
  const [selectedPaymentAdvance, setSelectedPaymentAdvance] = useState<string>("");
  const [isLoadingPaymentAdvances, setIsLoadingPaymentAdvances] = useState<boolean>(false);
  const [reinvestmentBalance, setReinvestmentBalance] = useState<number>(0);
  const [isLoadingReinvestmentBalance, setIsLoadingReinvestmentBalance] = useState<boolean>(false);

  const [proposedAddress, setProposedAddress] = useState<ProposedAddress | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      setIsLoadingData(true);
      try {
        // Get date range for routes (current month)
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
        const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];

        const [reasonsRes, saleTypesRes, paymentFormsRes, routesRes] = await Promise.all([
          getSaleChangeRequestReasons("edit"),
          getSelectSaleTypes(),
          getPaymentForms(),
          getCircuitRoutesData({ start_date: startDate, end_date: endDate }),
        ]);

        if (reasonsRes.data?.response) {
          setReasons(reasonsRes.data.response);
        }
        if (saleTypesRes.ok && saleTypesRes.data?.response) {
          setSaleTypes(saleTypesRes.data.response);
        }
        if (paymentFormsRes.ok && paymentFormsRes.data?.response) {
          setPaymentFormOptions(paymentFormsRes.data.response);
        }
        if (routesRes.ok && routesRes.data?.response) {
          setRoutes(routesRes.data.response);
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (sale && isOpen) {
      setProposedSaleTypeId(sale.sale_type_id);
      setProposedCircuitRouteId(sale.circuit_route_id || null);
      setProposedPaymentForms(
        sale.payment_forms?.map((pf) => ({
          payment_form_id: pf.payment_form_id,
          value: parseFloat(pf.value),
          ...(pf.payment_advance_id && { payment_advance_id: pf.payment_advance_id }),
        })) || []
      );

      const firstAddress = sale.client?.addresses?.[0];
      if (firstAddress) {
        const buildAddressName = (addr: Address) => {
          if (!addr) return "";
          const parts: string[] = [];
          if (addr.street) parts.push(addr.street);
          if (addr.number) parts.push(addr.number);
          if (addr.neighborhood) parts.push(addr.neighborhood);
          if (addr.city && addr.state) parts.push(`${addr.city}/${addr.state}`);
          else if (addr.city) parts.push(addr.city);
          return parts.join(", ");
        };

        setProposedAddress({
          addressName: buildAddressName(firstAddress),
          street: firstAddress.street || "",
          number: firstAddress.number || "",
          complement: firstAddress.complement || "",
          neighborhood: firstAddress.neighborhood || "",
          city: firstAddress.city || "",
          state: firstAddress.state || "",
          zip_code: firstAddress.zip_code || "",
          latitude: firstAddress.latitude ? Number(firstAddress.latitude) : null,
          longitude: firstAddress.longitude ? Number(firstAddress.longitude) : null,
        });
      }
    }
  }, [sale, isOpen]);

  useEffect(() => {
    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setIsGoogleMapsLoaded(true);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
      }
    };

    if (isOpen && proposedSaleTypeId === SALE_TYPE_ID.SEDEX) {
      loadMaps();
    }
  }, [isOpen, proposedSaleTypeId]);

  // Fetch payment advances when ADVANCE_PAYMENT is selected
  useEffect(() => {
    const fetchPaymentAdvances = async () => {
      if (newPaymentForm !== PAYMENT_FORMS.ADVANCE_PAYMENT) {
        setSelectedPaymentAdvance("");
        return;
      }

      if (!sale?.client_id || !sale?.seller_id) {
        setPaymentAdvances([]);
        return;
      }

      setIsLoadingPaymentAdvances(true);
      try {
        const { data, ok } = await getSelectPaymentAdvances({
          client_id: sale.client_id,
          seller_id: sale.seller_id,
        });
        if (ok && data?.response) {
          setPaymentAdvances(data.response);
        } else {
          setPaymentAdvances([]);
        }
      } catch (error) {
        console.error("Error fetching payment advances:", error);
        setPaymentAdvances([]);
      } finally {
        setIsLoadingPaymentAdvances(false);
      }
    };

    fetchPaymentAdvances();
  }, [newPaymentForm, sale?.client_id, sale?.seller_id]);

  // Fetch reinvestment balance when REINVESTMENT is selected
  useEffect(() => {
    const fetchReinvestmentBalance = async () => {
      if (newPaymentForm !== PAYMENT_FORMS.REINVESTMENT) {
        return;
      }

      if (!sale?.client?.uuid) {
        setReinvestmentBalance(0);
        return;
      }

      setIsLoadingReinvestmentBalance(true);
      try {
        // Busca o saldo TOTAL do cliente (sem filtro de balance_origin_type_id)
        const { data, ok } = await getClientBalance(sale.client.uuid);
        if (ok && data?.response !== undefined) {
          setReinvestmentBalance(Number(data.response) || 0);
        } else {
          setReinvestmentBalance(0);
        }
      } catch (error) {
        console.error("Error fetching reinvestment balance:", error);
        setReinvestmentBalance(0);
      } finally {
        setIsLoadingReinvestmentBalance(false);
      }
    };

    fetchReinvestmentBalance();
  }, [newPaymentForm, sale?.client?.uuid]);

  const handleClose = () => {
    setSelectedReason("");
    setOtherReason("");
    setProposedSaleTypeId(null);
    setProposedCircuitRouteId(null);
    setProposedPaymentForms([]);
    setNewPaymentForm(null);
    setNewPaymentValue("");
    setSelectedPaymentAdvance("");
    setPaymentAdvances([]);
    setReinvestmentBalance(0);
    setProposedAddress(null);
    onClose();
  };

  const totalSaleAmount = Number(sale?.total || 0);
  const totalProposedPayments = proposedPaymentForms.reduce(
    (sum, pf) => sum + pf.value,
    0
  );
  const remainingAmount = Number((totalSaleAmount - totalProposedPayments).toFixed(2));

  // Get IDs of payment advances already used in proposed payment forms
  const usedPaymentAdvanceIds = proposedPaymentForms
    .filter((f) => f.payment_form_id === PAYMENT_FORMS.ADVANCE_PAYMENT && f.payment_advance_id)
    .map((f) => f.payment_advance_id);

  // Filter available payment advances (exclude already used ones)
  const availablePaymentAdvances = paymentAdvances.filter(
    (advance) => !usedPaymentAdvanceIds.includes(advance.id)
  );

  const handleAddPaymentForm = () => {
    if (!newPaymentForm || !newPaymentValue) return;

    // Verificar se precisa de pix gerado selecionado para ADVANCE_PAYMENT
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && !selectedPaymentAdvance) {
      toast.error("Selecione um Pix Gerado");
      return;
    }

    let value: number;
    try {
      value = parseFloat(Mask.transformCurrencyInFloat(newPaymentValue));
      if (isNaN(value) || value <= 0) {
        toast.error("Valor inválido");
        return;
      }
    } catch (error) {
      toast.error("Valor inválido");
      return;
    }

    const tolerance = 0.01;

    // Verificar se o valor não excede o valor do pix gerado selecionado
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      const selectedAdvance = paymentAdvances.find(
        (advance) => advance.id.toString() === selectedPaymentAdvance
      );
      const advanceAmount = Number(selectedAdvance?.amount || 0);
      if (selectedAdvance && value > advanceAmount) {
        toast.error(
          `O valor não pode exceder o valor do Pix Gerado (${Mask.formatCurrency(advanceAmount)})`
        );
        return;
      }
    }

    // Verificar se o valor não excede o saldo de reinvestimento disponível
    if (newPaymentForm === PAYMENT_FORMS.REINVESTMENT) {
      // Calcular o total já usado de reinvestimento nos pagamentos propostos
      const totalReinvestmentUsed = proposedPaymentForms
        .filter((pf) => pf.payment_form_id === PAYMENT_FORMS.REINVESTMENT)
        .reduce((sum, pf) => sum + pf.value, 0);
      const availableReinvestment = reinvestmentBalance - totalReinvestmentUsed;

      if (value > availableReinvestment) {
        toast.error(
          `O valor não pode exceder o saldo disponível para reinvestimento (${Mask.formatCurrency(availableReinvestment)})`
        );
        return;
      }
    }

    if (value > remainingAmount && Math.abs(value - remainingAmount) > tolerance) {
      toast.error(
        `O valor não pode exceder o valor restante da venda (${Mask.formatCurrency(remainingAmount)})`
      );
      return;
    }

    if (Math.abs(value - remainingAmount) <= tolerance) {
      value = remainingAmount;
    }

    const paymentFormItem: ProposedPaymentForm = {
      payment_form_id: newPaymentForm,
      value,
    };

    // Adicionar ID do pix gerado se for ADVANCE_PAYMENT
    if (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT) {
      paymentFormItem.payment_advance_id = parseInt(selectedPaymentAdvance);
    }

    setProposedPaymentForms((prev) => [...prev, paymentFormItem]);
    setNewPaymentForm(null);
    setNewPaymentValue("");
    setSelectedPaymentAdvance("");
  };

  const handleRemovePaymentForm = (index: number) => {
    setProposedPaymentForms((prev) => prev.filter((_, i) => i !== index));
  };

  const hasChanges = () => {
    if (proposedSaleTypeId !== sale?.sale_type_id) return true;
    if (proposedCircuitRouteId !== (sale?.circuit_route_id || null)) return true;

    if (proposedSaleTypeId === SALE_TYPE_ID.SEDEX && proposedAddress) {
      const originalAddress = sale?.client?.addresses?.[0];
      if (!originalAddress) return true;
      if (proposedAddress.street !== (originalAddress.street || "")) return true;
      if (proposedAddress.number !== (originalAddress.number || "")) return true;
      if (proposedAddress.complement !== (originalAddress.complement || "")) return true;
      if (proposedAddress.neighborhood !== (originalAddress.neighborhood || "")) return true;
      if (proposedAddress.city !== (originalAddress.city || "")) return true;
      if (proposedAddress.state !== (originalAddress.state || "")) return true;
    }

    const originalForms = sale?.payment_forms || [];
    if (proposedPaymentForms.length !== originalForms.length) return true;

    for (const proposed of proposedPaymentForms) {
      const original = originalForms.find(
        (of) => of.payment_form_id === proposed.payment_form_id
      );
      if (!original || Math.abs(parseFloat(original.value) - proposed.value) > 0.01) {
        return true;
      }
    }

    return false;
  };

  const handleSubmit = async () => {
    if (!sale) return;

    if (!requestWindow.allowed) {
      toast.error(requestWindow.message ?? "O prazo para esta solicitação expirou");
      return;
    }

    const selectedReasonObj = reasons.find(
      (r) => r.id.toString() === selectedReason
    );
    const isOtherReason = selectedReasonObj?.name === "Outro";

    if (!selectedReason) {
      toast.error("Selecione um motivo para a solicitação");
      return;
    }

    if (isOtherReason && !otherReason.trim()) {
      toast.error("Descreva o motivo da solicitação");
      return;
    }

    if (!hasChanges()) {
      toast.error("Faça pelo menos uma alteração para enviar a solicitação");
      return;
    }

    if (remainingAmount !== 0) {
      toast.error("O total das formas de pagamento deve ser igual ao valor da venda");
      return;
    }

    setIsLoading(true);
    try {
      const response = await createEditRequest({
        sale_uuid: sale.uuid,
        reason_id: parseInt(selectedReason),
        reason_other: isOtherReason ? otherReason.trim() : undefined,
        sale_type_id: proposedSaleTypeId || undefined,
        circuit_route_id: proposedSaleTypeId === SALE_TYPE_ID.DELIVERY ? (proposedCircuitRouteId || undefined) : undefined,
        payment_forms: proposedPaymentForms,
        address: (proposedSaleTypeId === SALE_TYPE_ID.SEDEX || proposedSaleTypeId === SALE_TYPE_ID.DELIVERY) && proposedAddress ? proposedAddress : undefined,
      });

      if (response.ok) {
        toast.success("Solicitação de edição enviada com sucesso!");
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data?.message || "Erro ao enviar solicitação");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao enviar solicitação");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedReasonObj = reasons.find(
    (r) => r.id.toString() === selectedReason
  );
  const showOtherField = selectedReasonObj?.name === "Outro";

  const saleTypeChanged = proposedSaleTypeId !== sale?.sale_type_id;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto p-0">
        {/* Header com gradiente */}
        <DialogHeader className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-t-lg">
          <DialogTitle className="flex items-center gap-3 text-white text-xl">
            <div className="p-2 bg-white/20 rounded-lg">
              <Edit3 className="h-5 w-5" />
            </div>
            Solicitar Edição de Venda
          </DialogTitle>
          <p className="text-blue-100 text-sm mt-1">
            Sua solicitação será analisada por um gerente ou administrador
          </p>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Aviso de prazo expirado */}
            {!requestWindow.allowed && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Prazo para solicitar edição atingido
                  </p>
                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                    {requestWindow.message}
                  </p>
                </div>
              </div>
            )}

            {/* Card de Informações da Venda */}
            {sale && (
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    Informações da Venda
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Venda</p>
                    <p className="font-bold text-lg">#{sale.id}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Data
                    </p>
                    <p className="font-medium">
                      {new Date(sale.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 shadow-sm border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-600 dark:text-blue-400 mb-1 flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> Total
                    </p>
                    <p className="font-bold text-lg text-blue-700 dark:text-blue-300">
                      {Mask.formatCurrency(Number(sale.total))}
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm mt-4">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <User className="h-3 w-3" /> Cliente
                  </p>
                  <p className="font-medium">{sale.client?.name || "-"}</p>
                </div>
              </div>
            )}

            {/* Motivo da Edição */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-slate-500" />
                <Label className="text-base font-semibold">Motivo da edição</Label>
                <span className="text-red-500">*</span>
              </div>
              <RadioGroup
                value={selectedReason}
                onValueChange={setSelectedReason}
                className="grid grid-cols-1 gap-2"
              >
                {reasons.map((reason) => (
                  <div
                    key={reason.id}
                    className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedReason === reason.id.toString()
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                    onClick={() => setSelectedReason(reason.id.toString())}
                  >
                    <RadioGroupItem
                      value={reason.id.toString()}
                      id={`edit-reason-${reason.id}`}
                      className="border-2"
                    />
                    <Label
                      htmlFor={`edit-reason-${reason.id}`}
                      className="flex-1 cursor-pointer font-medium"
                    >
                      {reason.name}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {showOtherField && (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="edit-other-reason" className="text-sm">
                    Descreva o motivo <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="edit-other-reason"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Descreva detalhadamente o motivo da edição..."
                    className="min-h-[80px] resize-none"
                  />
                </div>
              )}
            </div>

            {/* Alterações Propostas */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Edit3 className="h-4 w-4 text-slate-500" />
                <h4 className="font-semibold text-base">Alterações Propostas</h4>
              </div>

              {/* Tipo de Venda */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Store className="h-4 w-4 text-slate-500" />
                  <Label className="font-medium">Tipo de Venda</Label>
                </div>
                <Select
                  value={proposedSaleTypeId?.toString() || ""}
                  onValueChange={(value) => setProposedSaleTypeId(Number(value))}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-800">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {saleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {saleTypeChanged && (
                  <div className="flex items-center gap-2 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg">
                    <AlertCircle className="h-4 w-4" />
                    <span>
                      <strong>{sale?.sale_type?.name}</strong> → <strong>{saleTypes.find((t) => t.id === proposedSaleTypeId)?.name}</strong>
                    </span>
                  </div>
                )}
              </div>

              {/* Rota (apenas para tipo Entrega) */}
              {proposedSaleTypeId === SALE_TYPE_ID.DELIVERY && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-slate-500" />
                    <Label className="font-medium">Rota de Entrega</Label>
                  </div>
                  <Select
                    value={proposedCircuitRouteId?.toString() || ""}
                    onValueChange={(value) => setProposedCircuitRouteId(Number(value))}
                  >
                    <SelectTrigger className="bg-white dark:bg-slate-800">
                      <SelectValue placeholder="Selecione a rota" />
                    </SelectTrigger>
                    <SelectContent>
                      {routes.map((route) => (
                        <SelectItem key={route.id} value={route.id.toString()}>
                          {route.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {proposedCircuitRouteId !== (sale?.circuit_route_id || null) && (
                    <div className="flex items-center gap-2 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded-lg">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        <strong>{sale?.circuit_route?.title || "Sem rota"}</strong> → <strong>{routes.find((r) => r.id === proposedCircuitRouteId)?.title || "Sem rota"}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Endereço de Entrega (para Sedex) */}
              {proposedSaleTypeId === SALE_TYPE_ID.SEDEX && (
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    <Label className="font-medium">Endereço de Entrega</Label>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Buscar Endereço</Label>
                    <AddressSearch
                      onAddressSelect={(selectedAddress) => {
                        setProposedAddress({
                          addressName: selectedAddress.addressName,
                          street: selectedAddress.street,
                          number: selectedAddress.number,
                          complement: proposedAddress?.complement || "",
                          neighborhood: selectedAddress.neighborhood,
                          city: selectedAddress.city,
                          state: selectedAddress.state,
                          zip_code: selectedAddress.zip_code,
                          latitude: parseFloat(selectedAddress.latitude) || null,
                          longitude: parseFloat(selectedAddress.longitude) || null,
                        });
                      }}
                      defaultValue=""
                      placeholder="Digite o endereço para buscar..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Complemento</Label>
                      <Input
                        value={proposedAddress?.complement || ""}
                        onChange={(e) =>
                          setProposedAddress((prev) =>
                            prev
                              ? { ...prev, complement: e.target.value }
                              : null
                          )
                        }
                        placeholder="Complemento"
                        className="bg-white dark:bg-slate-800"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Ponto de Referência</Label>
                      <Input
                        value={proposedAddress?.reference || ""}
                        onChange={(e) =>
                          setProposedAddress((prev) =>
                            prev
                              ? { ...prev, reference: e.target.value }
                              : null
                          )
                        }
                        placeholder="Ponto de referência"
                        className="bg-white dark:bg-slate-800"
                      />
                    </div>
                  </div>

                  {proposedAddress?.latitude && proposedAddress?.longitude && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Mapa</Label>
                        {isGoogleMapsLoaded ? (
                          <GoogleMap
                            latitude={Number(proposedAddress.latitude)}
                            longitude={Number(proposedAddress.longitude)}
                            address={proposedAddress.addressName}
                            className="h-48 rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-48 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center">
                            <div className="flex items-center gap-2 text-gray-500">
                              <Loader2 className="h-5 w-5 animate-spin" />
                              <span>Carregando mapa...</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {proposedAddress?.addressName && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
                          <p className="text-blue-700 dark:text-blue-300">
                            <span className="font-medium">Endereço selecionado:</span> {proposedAddress.addressName}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Formas de Pagamento */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-slate-500" />
                  <Label className="font-medium">Formas de Pagamento</Label>
                </div>

                {/* Lista de formas de pagamento */}
                {proposedPaymentForms.length > 0 && (
                  <div className="space-y-2">
                    {proposedPaymentForms.map((form, index) => {
                      const formName =
                        paymentFormOptions.find((pf) => pf.id === form.payment_form_id)
                          ?.name || "Desconhecido";
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                              <CreditCard className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium">{formName}</p>
                              <p className="text-sm text-green-600 font-semibold">
                                {Mask.formatCurrency(form.value)}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleRemovePaymentForm(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Adicionar nova forma */}
                <div className="flex gap-2">
                  <Select
                    value={newPaymentForm?.toString() || ""}
                    onValueChange={(value) => setNewPaymentForm(Number(value))}
                  >
                    <SelectTrigger className="flex-1 bg-white dark:bg-slate-800">
                      <SelectValue placeholder="Selecionar forma" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentFormOptions
                        .filter((form) => {
                          // Não mostrar Vale Compra se o cliente não tiver autorização
                          if (form.id === PAYMENT_FORMS.VALE) {
                            return sale?.client?.has_purchase_voucher === true;
                          }
                          return true;
                        })
                        .map((form) => (
                        <SelectItem key={form.id} value={form.id.toString()}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    typeMask="currency"
                    value={newPaymentValue}
                    onChange={(e) => setNewPaymentValue(e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-32 bg-white dark:bg-slate-800"
                    readOnly={newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && !!selectedPaymentAdvance}
                  />
                  <Button
                    type="button"
                    onClick={handleAddPaymentForm}
                    disabled={
                      !newPaymentForm ||
                      !newPaymentValue ||
                      (newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && !selectedPaymentAdvance)
                    }
                    size="icon"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Selecionar Pix Gerado para Pagamento Antecipado */}
                {newPaymentForm === PAYMENT_FORMS.ADVANCE_PAYMENT && (
                  <div className="grid gap-2 mt-2">
                    <Label className="flex items-center gap-2 text-sm">
                      <QrCode className="h-4 w-4" />
                      Selecione o Pix Gerado
                    </Label>
                    {isLoadingPaymentAdvances ? (
                      <div className="text-sm text-gray-500">Carregando pix gerados...</div>
                    ) : !sale?.client_id || !sale?.seller_id ? (
                      <div className="text-sm text-amber-600">
                        Dados do cliente/vendedor não disponíveis
                      </div>
                    ) : paymentAdvances.length === 0 ? (
                      <div className="text-sm text-amber-600">
                        Nenhum Pix Gerado disponível para este cliente e vendedor
                      </div>
                    ) : availablePaymentAdvances.length === 0 ? (
                      <div className="text-sm text-amber-600">
                        Todos os Pix Gerados disponíveis já foram utilizados
                      </div>
                    ) : (
                      <Select
                        value={selectedPaymentAdvance}
                        onValueChange={(value) => {
                          setSelectedPaymentAdvance(value);
                          const selectedAdvance = availablePaymentAdvances.find(
                            (advance) => advance.id.toString() === value
                          );
                          if (selectedAdvance) {
                            setNewPaymentValue(Mask.formatCurrency(Number(selectedAdvance.amount)));
                          }
                        }}
                      >
                        <SelectTrigger className="bg-white dark:bg-slate-800">
                          <SelectValue placeholder="Selecione o Pix Gerado" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePaymentAdvances.map((advance) => (
                            <SelectItem key={advance.id} value={advance.id.toString()}>
                              #{advance.id} - {Mask.formatCurrency(Number(advance.amount))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {selectedPaymentAdvance && (
                      <div className="text-sm text-green-600 mt-1">
                        Pix Gerado selecionado: {Mask.formatCurrency(
                          Number(paymentAdvances.find(
                            (advance) => advance.id.toString() === selectedPaymentAdvance
                          )?.amount || 0)
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Saldo de Reinvestimento */}
                {newPaymentForm === PAYMENT_FORMS.REINVESTMENT && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 mt-2">
                    <DollarSign className="h-4 w-4" />
                    {isLoadingReinvestmentBalance ? (
                      <span className="text-sm">Carregando saldo...</span>
                    ) : !sale?.client?.uuid ? (
                      <span className="text-sm text-amber-600">
                        Dados do cliente não disponíveis
                      </span>
                    ) : (
                      <span className="text-sm font-medium">
                        Saldo disponível para reinvestimento: {Mask.formatCurrency(reinvestmentBalance)}
                      </span>
                    )}
                  </div>
                )}

                {/* Status do pagamento */}
                <div
                  className={`flex items-center gap-2 p-3 rounded-lg ${
                    remainingAmount === 0
                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                      : remainingAmount > 0
                      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                      : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                  }`}
                >
                  {remainingAmount === 0 ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : remainingAmount > 0 ? (
                    <AlertCircle className="h-5 w-5" />
                  ) : (
                    <XCircle className="h-5 w-5" />
                  )}
                  <span className="font-medium">
                    {remainingAmount === 0
                      ? "Pagamento completo"
                      : remainingAmount > 0
                      ? `Falta: ${Mask.formatCurrency(remainingAmount)}`
                      : `Excede em: ${Mask.formatCurrency(Math.abs(remainingAmount))}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="p-6 pt-0 gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 sm:flex-none"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isLoading ||
              !selectedReason ||
              remainingAmount !== 0 ||
              !requestWindow.allowed
            }
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <Edit3 className="h-4 w-4 mr-2" />
                Solicitar Edição
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
